// web/src/pages/RGPD.jsx
import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import HeaderAuth from "../components/common/HeaderAuth";
import { Link } from "react-router-dom";

export default function RGPD() {
  const origin =
    (typeof window !== "undefined" && window.location?.origin) ||
    "https://etatdulieu.com";

  const pageUrl = `${origin}/rgpd`;
  const mail = "contact.etatdulieu@gmail.com";

  useEffect(() => {
    try {
      document.documentElement.style.colorScheme = "light";
    } catch {}
  }, []);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "Politique de confidentialité — État du Lieu",
    url: pageUrl,
    isPartOf: { "@type": "WebSite", name: "État du Lieu", url: origin },
    dateModified: "2026-06-22",
  };

  return (
    <div className="legal-page">
      <Helmet prioritizeSeoTags>
        <link rel="canonical" href={pageUrl} />
        <title>Politique de confidentialité (RGPD) — État du Lieu</title>
        <meta
          name="description"
          content="Politique de confidentialité d’État du Lieu : compte utilisateur, historique, mesure d’audience, publicité Google AdSense, consentement, cookies, sous-traitants et droits des utilisateurs."
        />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Politique de confidentialité (RGPD) — État du Lieu" />
        <meta
          property="og:description"
          content="Découvrez comment État du Lieu traite vos données personnelles, gère la publicité Google AdSense et vous permet d’exercer vos droits."
        />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:image" content={`${origin}/og/home.png`} />
        <meta property="og:locale" content="fr_FR" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <style>{`
        :root{
          --bg1:#3b82f6; --bg2:#1e40af;
          --surface:rgba(255,255,255,0.95);
          --ink:#0f172a; --muted:#64748b;
          --container-pad:16px;
          --auth-reserve:88px;
        }
        @supports (padding: max(0px)){
          :root{ --auth-reserve: max(76px, calc(env(safe-area-inset-top, 0px) + 64px)); }
        }
        html, body { margin:0; padding:0; color-scheme: light; }
        .legal-page{
          min-height:100vh;
          background: linear-gradient(135deg,var(--bg1) 0%, var(--bg2) 100%);
          color: var(--ink);
          font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Oxygen,Ubuntu,Cantarell,sans-serif;
          position: relative;
        }
        .wrap{
          max-width:1000px;
          margin:0 auto;
          padding: calc(28px + var(--auth-reserve)) var(--container-pad) 64px;
        }
        .glass-chip{
          background: var(--surface);
          backdrop-filter: blur(18px);
          border: 1px solid rgba(255,255,255,0.22);
          border-radius: 14px;
          padding: 8px 12px;
          box-shadow: 0 12px 28px rgba(0,0,0,0.12);
          display: inline-flex;
          align-items:center;
          gap:10px;
          color: var(--ink);
          text-decoration: none;
          font-weight: 600;
        }
        .back{ margin: 6px 0 16px; }

        .card{
          background: var(--surface);
          backdrop-filter: blur(20px);
          border-radius: 20px;
          padding: 28px 24px 28px;
          border: 1px solid rgba(255,255,255,0.22);
          box-shadow: 0 20px 40px rgba(0,0,0,0.08);
        }
        h1{ font-size: clamp(2rem, 4vw, 2.6rem); margin: 0 0 8px; letter-spacing:-.01em; }
        h2{ font-size:1.25rem; margin:20px 0 8px; }
        h3{ font-size:1.05rem; margin:14px 0 6px; }
        p{ color:var(--muted); line-height:1.7; margin: .4rem 0 .9rem; }
        ul,ol{ color:var(--muted); padding-left:1.3rem; margin:.25rem 0 .8rem; line-height:1.7; }
        li{ margin:.15rem 0; }
        strong{ color:#111827; font-weight:600; }
        a{ color:#2563eb; text-decoration:none; }
        a:hover{ text-decoration:underline; }
        .subtitle{ font-size:.96rem; color:var(--muted); margin:0 0 12px; }
        .section-label{ font-size:.8rem; font-weight:600; letter-spacing:.08em; text-transform:uppercase; color:#9ca3af; margin-bottom:4px; }
        .toc{ margin:10px 0 18px; padding-left:18px; font-size:.95rem; }
        .toc li{ margin:4px 0; }
        @media (max-width: 640px){
          :root, html, body, .legal-page{ color-scheme: light; background:#ffffff; }
          .card{ background:#fff !important; }
        }
      `}</style>

      <div className="wrap">
        <HeaderAuth floating top={12} right={12} />
        <div className="back">
          <Link className="glass-chip back-chip" to="/">← Retour</Link>
        </div>

        <article className="card" aria-label="Politique de confidentialité">
          <header>
            <div className="section-label">RGPD &amp; Données personnelles</div>
            <h1>Politique de confidentialité - RGPD</h1>
            <p className="subtitle">
              La présente politique décrit la manière dont État du Lieu collecte, utilise, conserve
              et protège vos données personnelles conformément au RGPD et à la loi Informatique et Libertés.
            </p>
            <p>Dernière mise à jour : <strong>22 juin 2026</strong></p>
          </header>

          <nav aria-label="Sommaire">
            <ol className="toc">
              <li><a href="#responsable">1. Identité du responsable du traitement</a></li>
              <li><a href="#traitements">2. Données collectées, finalités et bases légales</a></li>
              <li><a href="#hebergement">3. Hébergement et sécurité</a></li>
              <li><a href="#destinataires">4. Destinataires et sous-traitants</a></li>
              <li><a href="#transferts">5. Transferts de données hors de l’Union européenne</a></li>
              <li><a href="#droits">6. Droits des utilisateurs</a></li>
              <li><a href="#decision">7. Prise de décision automatisée</a></li>
              <li><a href="#violation">8. Notification en cas de violation de données</a></li>
              <li><a href="#maj">9. Mises à jour de la politique</a></li>
            </ol>
          </nav>

          <section id="responsable">
            <h2>1. Identité du responsable du traitement</h2>
            <p>
              Responsable du traitement : <strong>Antoine CHOUTEAU</strong> – Entreprise individuelle (micro-entreprise).
            </p>
            <p>
              Siège social : <strong>14550 Blainville-sur-Orne – France</strong><br />
              SIRET : <strong>99336134400019</strong><br />
              Adresse e-mail de contact : <a href={`mailto:${mail}`}>{mail}</a>
            </p>
          </section>

          <section id="traitements">
            <h2>2. Données collectées, finalités et bases légales des traitements</h2>
            <p>
              Les données sont collectées uniquement dans la mesure nécessaire au bon fonctionnement du service,
              à la gestion de votre compte, à l’amélioration du site, à l’affichage de publicités
              et au respect de nos obligations légales.
            </p>

            <h3>a) Création et gestion du compte utilisateur</h3>
            <p>Lors de la création d’un compte, nous pouvons collecter :</p>
            <ul>
              <li>votre adresse e-mail ;</li>
              <li>un mot de passe stocké sous forme hachée (jamais en clair) ;</li>
              <li>le cas échéant, vos préférences, favoris et historique de rapports.</li>
            </ul>
            <p>
              <strong>Finalité :</strong> création, gestion et sécurisation de votre espace personnel,
              sauvegarde de certains usages du service.<br />
              <strong>Base légale :</strong> exécution du contrat ou de mesures précontractuelles à votre demande.<br />
              <strong>Durée de conservation :</strong> jusqu’à 3 ans après la dernière activité du compte,
              puis suppression ou anonymisation, sauf obligation légale contraire.
            </p>

            <h3>b) Utilisation du service et génération des rapports</h3>
            <p>
              Lorsque vous utilisez le site, nous traitons les informations nécessaires à la recherche d’adresses,
              à l’affichage des résultats, à la génération des rapports et au bon fonctionnement technique du service.
            </p>
            <ul>
              <li>adresse ou identifiant recherché ;</li>
              <li>données techniques nécessaires au fonctionnement du site et à la sécurité ;</li>
              <li>historique local ou associé au compte lorsque cette fonctionnalité est utilisée.</li>
            </ul>
            <p>
              Le widget partenaire transmet directement la recherche à État du Lieu : le site partenaire ne reçoit
              ni le texte saisi ni le rapport. Un lien de partage anonyme ne conserve que l’identifiant BAN, des
              jetons aléatoires stockés sous forme hachée et les dates techniques de création ou de révocation.
            </p>
            <p>
              <strong>Finalité :</strong> fournir le service demandé, améliorer l’expérience utilisateur,
              prévenir les abus et assurer la sécurité de la plateforme.<br />
              <strong>Base légale :</strong> exécution du service et intérêt légitime pour la sécurité et la maintenance.<br />
              <strong>Durée de conservation :</strong> variable selon la nature des données ; limitée au temps nécessaire
              au service et à la sécurité, puis suppression ou anonymisation.
            </p>

            <h3>c) Mesure d’audience sans cookies (Umami)</h3>
            <p>
              Nous utilisons <strong>Umami Analytics</strong>, auto-hébergé, dans une configuration visant
              à limiter le recours aux traceurs et à réduire l’identification des visiteurs.
            </p>
            <p>
              Peuvent être traitées, sous forme agrégée ou pseudonymisée selon la configuration en place :
              pages consultées, événements, référents, horodatage, type d’appareil ou de navigateur,
              et zone géographique approximative.
            </p>
            <p>
              Les événements du widget peuvent inclure le code partenaire et le type d’interaction. Le texte libre
              saisi dans le champ d’adresse n’est jamais envoyé dans ces événements de mesure.
            </p>
            <p>
              <strong>Finalité :</strong> mesure d’audience, compréhension de l’usage du site, amélioration du service.<br />
              <strong>Base légale :</strong> intérêt légitime, sous réserve du respect des conditions applicables
              à cette mesure d’audience.<br />
              <strong>Durée :</strong> durée limitée au strict nécessaire, et au plus 13 mois pour les données concernées.
            </p>

            <h3>d) Publicité, cookies et gestion du consentement (Google AdSense)</h3>
            <p>
              Le site peut afficher des espaces publicitaires fournis par <strong>Google AdSense</strong>.
              Selon votre choix de consentement et le type d’annonce affichée, Google et, le cas échéant,
              certains partenaires publicitaires peuvent utiliser des cookies ou des technologies similaires
              et traiter notamment les catégories de données suivantes :
            </p>
            <ul>
              <li>adresse IP et zone géographique approximative qui peut en être déduite ;</li>
              <li>informations sur l’appareil, le système, le navigateur et la langue ;</li>
              <li>cookies, identifiants publicitaires ou autres identifiants en ligne ;</li>
              <li>pages consultées, site ou page d’origine, date et heure de la visite ;</li>
              <li>interactions avec les annonces, telles que leur affichage ou un clic.</li>
            </ul>
            <p>Ces données peuvent être utilisées afin :</p>
            <ul>
              <li>d’afficher des annonces personnalisées, non personnalisées ou limitées ;</li>
              <li>de mesurer la diffusion et la performance publicitaires ;</li>
              <li>de limiter la fréquence d’affichage ;</li>
              <li>de lutter contre la fraude et les abus.</li>
            </ul>
            <p>
              Pour en savoir plus sur l’utilisation des données par Google lorsque vous utilisez le Site,
              consultez la page{" "}
              <a
                href="https://policies.google.com/technologies/partner-sites?hl=fr"
                target="_blank"
                rel="noreferrer"
              >
                Comment Google utilise les informations provenant de sites ou d’applications qui utilisent ses services
              </a>.
            </p>
            <p>
              Le recueil de votre choix est géré via la plateforme de consentement mise en place sur le site.
              Vous pouvez accepter, refuser ou modifier vos choix à tout moment via le mécanisme prévu à cet effet sur le site.
            </p>
            <p>
              <strong>Finalité :</strong> financement du service par la publicité, mesure publicitaire,
              sécurité et prévention de la fraude.<br />
              <strong>Base légale :</strong> consentement lorsque celui-ci est requis ; intérêt légitime
              pour certaines opérations strictement nécessaires à la sécurité, à la prévention de la fraude
              ou au bon fonctionnement technique, dans les limites prévues par la réglementation applicable.<br />
              <strong>Durée :</strong> variable selon les cookies, technologies ou données concernées
              et selon les règles des prestataires impliqués.
            </p>

            <h3>e) Support et contact</h3>
            <p>
              Lorsque vous nous contactez par e-mail, nous traitons les informations que vous nous transmettez
              afin de répondre à votre demande.
            </p>
            <p>
              <strong>Finalité :</strong> gestion des demandes, support utilisateur, traitement des réclamations.<br />
              <strong>Base légale :</strong> intérêt légitime ou exécution de mesures précontractuelles / contractuelles selon le cas.<br />
              <strong>Durée :</strong> temps nécessaire au traitement de la demande, puis archivage limité si nécessaire.
            </p>
          </section>

          <section id="hebergement">
            <h2>3. Hébergement et sécurité</h2>
            <p>
              Hébergeur principal : <strong>OVHcloud</strong>, 2 rue Kellermann – 59100 Roubaix – France.
            </p>
            <ul>
              <li>chiffrement des échanges (TLS) ;</li>
              <li>mots de passe stockés sous forme hachée ;</li>
              <li>contrôle des accès ;</li>
              <li>sauvegardes et mesures de sécurité raisonnables adaptées au service.</li>
            </ul>
            <p>
              Malgré ces précautions, aucun système n’offre une sécurité absolue. Nous nous efforçons toutefois
              de mettre en œuvre des mesures appropriées au regard des risques.
            </p>
          </section>

          <section id="destinataires">
            <h2>4. Destinataires et sous-traitants</h2>
            <p>
              Vos données peuvent être accessibles, dans la limite de leurs attributions respectives,
              aux catégories suivantes :
            </p>
            <ul>
              <li>l’éditeur du site et les personnes habilitées à assurer l’exploitation technique et le support ;</li>
              <li>l’hébergeur et les prestataires techniques nécessaires au fonctionnement du service ;</li>
              <li>les prestataires de mesure d’audience utilisés sur le site ;</li>
              <li>Google, dans le cadre de Google AdSense et de la gestion du consentement publicitaire ;</li>
              <li>le cas échéant, les partenaires publicitaires affichés ou référencés dans l’interface de consentement applicable.</li>
            </ul>
            <p>
              Ces prestataires agissent selon leur rôle propre : sous-traitants, responsables conjoints
              ou responsables de traitement indépendants selon le service concerné et les obligations légales qui leur incombent.
            </p>
          </section>

          <section id="transferts">
            <h2>5. Transferts de données hors de l’Union européenne</h2>
            <p>
              Certains prestataires techniques, analytiques ou publicitaires peuvent impliquer des transferts
              de données hors de l’Espace économique européen.
            </p>
            <p>
              Lorsque de tels transferts existent, ils sont encadrés par les mécanismes juridiques appropriés
              prévus par la réglementation applicable, par exemple des clauses contractuelles types,
              une décision d’adéquation ou tout autre mécanisme reconnu.
            </p>
          </section>

          <section id="droits">
            <h2>6. Droits des utilisateurs</h2>
            <p>Vous disposez, selon les cas, des droits suivants :</p>
            <ul>
              <li>droit d’accès ;</li>
              <li>droit de rectification ;</li>
              <li>droit à l’effacement ;</li>
              <li>droit à la limitation du traitement ;</li>
              <li>droit d’opposition ;</li>
              <li>droit à la portabilité ;</li>
              <li>droit de retirer votre consentement à tout moment lorsqu’un traitement repose sur celui-ci.</li>
            </ul>
            <p>
              Pour exercer vos droits, vous pouvez nous écrire à <a href={`mailto:${mail}`}>{mail}</a>.
              Vous pouvez également modifier vos choix relatifs à la publicité et au consentement
              via le mécanisme prévu à cet effet sur le site.
            </p>
            <p>
              Vous pouvez enfin introduire une réclamation auprès de la{" "}
              <a href="https://www.cnil.fr" target="_blank" rel="noreferrer">
                CNIL
              </a>.
            </p>
          </section>

          <section id="decision">
            <h2>7. Prise de décision automatisée</h2>
            <p>
              État du Lieu ne met pas en œuvre de prise de décision automatisée produisant à elle seule
              des effets juridiques significatifs à votre égard au sens du RGPD.
            </p>
          </section>

          <section id="violation">
            <h2>8. Notification en cas de violation de données</h2>
            <p>
              En cas de violation de données personnelles présentant un risque pour les droits
              et libertés des personnes concernées, nous mettrons en œuvre les démarches requises
              par la réglementation applicable, y compris la notification à l’autorité compétente
              et, lorsque nécessaire, l’information des personnes concernées.
            </p>
          </section>

          <section id="maj">
            <h2>9. Mises à jour de la politique</h2>
            <p>
              La présente politique peut évoluer afin de refléter les modifications du site,
              de ses fonctionnalités, de ses prestataires ou de la réglementation applicable.
              La date de mise à jour figure en haut de page.
            </p>
          </section>
        </article>
      </div>
    </div>
  );
}
